import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterBar, { type FilterState } from '@/pages/category/components/FilterBar';
import FilmCard from '@/components/feature/FilmCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import { getAllFilms, getYears, FILM_TYPES } from '@/lib/catalog';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const DEFAULT_FILTER: FilterState = { type: '全部', year: '全部', sort: 'updated' };

export default function Category() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || '全部';
  const [filter, setFilter] = useState<FilterState>({
    ...DEFAULT_FILTER,
    type: FILM_TYPES.includes(initialType) ? initialType : '全部',
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = searchParams.get('type') || '全部';
    setFilter((prev) => ({ ...prev, type: FILM_TYPES.includes(t) ? t : '全部' }));
  }, [searchParams]);

  usePageMeta({
    title:
      filter.type === '全部'
        ? '神宫寺奈绪影视作品 - 全部作品 | 神宫寺奈绪影视内容站'
        : `神宫寺奈绪影视作品 - ${filter.type} | 神宫寺奈绪影视内容站`,
    description: `浏览神宫寺奈绪相关影视作品列表，支持按电影、电视剧、综艺分类筛选，按年份与更新时间排序，快速找到想看的作品。`,
    keywords: '神宫寺奈绪,影视作品列表,电影,电视剧,综艺',
  });

  const allFilms = getAllFilms();
  const years = getYears();

  const filtered = useMemo(() => {
    let list = allFilms.filter((f) => {
      const typeOk = filter.type === '全部' || f.type === filter.type;
      const yearOk = filter.year === '全部' || String(f.year) === filter.year;
      return typeOk && yearOk;
    });
    list = [...list].sort((a, b) => {
      if (filter.sort === 'year') return b.year - a.year;
      if (filter.sort === 'title') return a.title.localeCompare(b.title, 'zh-Hans-CN');
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return list;
  }, [allFilms, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleChange = (next: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...next }));
    setPage(1);
  };

  const handleReset = () => {
    setFilter(DEFAULT_FILTER);
    setPage(1);
  };

  return (
    <div className="w-full">
      <section className="px-4 md:px-6 pt-8 md:pt-12 pb-6 md:pb-8 bg-background-100 border-b border-background-200">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视作品' }]} />
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          影视作品库
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-2xl">
          汇集神宫寺奈绪出演的电影、电视剧与综艺节目，支持按类型、年份筛选与多维排序，帮助您快速定位想看的作品。
        </p>
      </section>

      <section className="px-4 md:px-6 py-8 md:py-10">
        <FilterBar value={filter} years={years} onChange={handleChange} onReset={handleReset} />

        <div className="mt-6 md:mt-8 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-accent-600 font-medium">{filtered.length}</span> 部作品
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {pageItems.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
            <div className="mt-10">
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="mt-10 py-16 text-center bg-background-100 border border-background-200 rounded-lg">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
              <i className="ri-inbox-line"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
              没有符合条件的作品
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              试试调整筛选条件，或点击下方按钮重置全部筛选
            </p>
            <button
              onClick={handleReset}
              className="mt-6 inline-flex px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium items-center gap-2 whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>
    </div>
  );
}